/**
 * CategoryMultiPicker — the product's PRIMARY category (required) plus optional
 * EXTRA categories (PIM Wave P2, Task 5). Replaces the editor's single category
 * `<select>`.
 *
 * Spec Q8/Q9: a product has exactly one primary category (drives the form
 * schema + the publish validation gate) and zero-or-more extras (discovery
 * only). Both primary and extras are LEAF, non-archived categories — the public
 * `GET /marketplace/categories` tree already excludes archived, so leaf-of-tree
 * is the whole filter. The primary is never also an extra, and extras are
 * de-duplicated.
 *
 * The wire contract (Task 6): `primary_category_id` + `category_ids`
 * (`category_ids` = the full set, primary included) ride the seller product
 * write; `category_id` stays in sync = primary (the backend dual-writes).
 */
import { useMemo, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@garden/ui";

import { SELLER_DASHBOARD_NS } from "@/features/seller-dashboard/api";
import {
  categoryDisplayName,
  findNodeInTree,
  type Category,
} from "@/features/marketplace/useCategoryTree";

/** One indented `<select>` option — leaf rows are selectable, branches disabled. */
interface CategoryOption {
  id: string;
  label: string;
  leaf: boolean;
}

/** Flatten the tree to indented options (branches disabled, leaves selectable),
 * mirroring the editor's prior `leafCategoryOptions`. */
function categoryOptions(
  tree: readonly Category[],
  language: string,
  depth = 0,
): CategoryOption[] {
  const out: CategoryOption[] = [];
  for (const node of tree) {
    const leaf = node.children.length === 0;
    out.push({
      id: node.id,
      label: `${"  ".repeat(depth)}${categoryDisplayName(node, language)}`,
      leaf,
    });
    if (!leaf) out.push(...categoryOptions(node.children, language, depth + 1));
  }
  return out;
}

export interface CategoryMultiPickerProps {
  tree: Category[];
  language: string;
  /** Selected primary category id, or "" when none. */
  primaryId: string;
  /** Selected extra category ids (never includes the primary). */
  extraIds: string[];
  onPrimaryChange: (id: string) => void;
  onExtrasChange: (ids: string[]) => void;
  /** Resolved primary-required error message, or undefined. */
  primaryError?: string;
  disabled?: boolean;
}

export function CategoryMultiPicker({
  tree,
  language,
  primaryId,
  extraIds,
  onPrimaryChange,
  onExtrasChange,
  primaryError,
  disabled,
}: CategoryMultiPickerProps): ReactElement {
  const { t } = useTranslation(SELLER_DASHBOARD_NS);
  const options = useMemo(() => categoryOptions(tree, language), [tree, language]);

  // Extras a seller may still add: leaf, not the primary, not already an extra.
  const addableOptions = useMemo(
    () =>
      options.filter(
        (o) => o.leaf && o.id !== primaryId && !extraIds.includes(o.id),
      ),
    [options, primaryId, extraIds],
  );

  const extraLabel = (id: string): string => {
    const node = findNodeInTree(tree, id);
    return node ? categoryDisplayName(node, language) : id;
  };

  const addExtra = (id: string): void => {
    if (id === "" || id === primaryId || extraIds.includes(id)) return;
    onExtrasChange([...extraIds, id]);
  };

  const removeExtra = (id: string): void => {
    onExtrasChange(extraIds.filter((x) => x !== id));
  };

  return (
    <div className="flex flex-col gap-3" data-testid="category-multi-picker">
      {/* Primary (required) */}
      <div className="flex flex-col gap-1">
        <label htmlFor="primary-category" className="text-sm font-medium text-foreground">
          {t("editor.category.primary")}
        </label>
        <select
          id="primary-category"
          data-testid="primary-category-select"
          value={primaryId}
          disabled={disabled}
          aria-invalid={primaryError ? true : undefined}
          onChange={(e) => onPrimaryChange(e.target.value)}
          className={[
            "min-h-[44px] rounded-[var(--g-radius)] border bg-card px-3 text-sm text-foreground",
            primaryError ? "border-[var(--g-error)]" : "border-border",
          ].join(" ")}
        >
          <option value="">{t("editor.category.primaryPlaceholder")}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id} disabled={!o.leaf}>
              {o.label}
            </option>
          ))}
        </select>
        {primaryError ? (
          <p role="alert" className="text-xs font-medium text-[var(--g-error)]">
            {primaryError}
          </p>
        ) : null}
      </div>

      {/* Extras (optional) */}
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-foreground">
          {t("editor.category.extras")}
        </span>
        <p className="text-xs text-muted-foreground">{t("editor.category.extrasHint")}</p>
        {extraIds.length > 0 ? (
          <div className="flex flex-wrap gap-2" data-testid="extra-category-chips">
            {extraIds.map((id) => (
              <span
                key={id}
                className="inline-flex h-8 items-center gap-1.5 rounded-full bg-secondary px-3 text-sm text-secondary-foreground"
              >
                {extraLabel(id)}
                <button
                  type="button"
                  onClick={() => removeExtra(id)}
                  disabled={disabled}
                  aria-label={t("editor.category.removeExtra", { name: extraLabel(id) })}
                >
                  <Icon name="x" size={12} />
                </button>
              </span>
            ))}
          </div>
        ) : null}
        <select
          aria-label={t("editor.category.addExtra")}
          data-testid="add-extra-category"
          value=""
          disabled={disabled || addableOptions.length === 0}
          onChange={(e) => {
            addExtra(e.target.value);
            e.currentTarget.value = "";
          }}
          className="min-h-[44px] max-w-full rounded-[var(--g-radius)] border border-border bg-card px-3 text-sm text-foreground"
        >
          <option value="">{t("editor.category.addExtra")}</option>
          {addableOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export default CategoryMultiPicker;
